import React, { useState, useEffect, useRef } from 'react';
import { User, Calendar, X, AlertCircle, Check } from 'lucide-react';
import './PatientDetailsModal.css';

const PatientDetailsModal = ({ open, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName('');
      setAge('');
      setGender('');
      setErrors({});
      setTimeout(() => nameRef.current && nameRef.current.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const handleContinue = (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = 'Please enter the patient name.';
    if (!age.trim()) nextErrors.age = 'Please enter the patient age.';
    else if (!/^\d+$/.test(age.trim())) nextErrors.age = 'Age must be a valid number.';
    else {
      const n = parseInt(age, 10);
      if (n < 1 || n > 120) nextErrors.age = 'Age must be between 1 and 120.';
    }
    if (!gender) nextErrors.gender = 'Please select the patient gender.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      onSubmit({ patientName: name.trim(), age: parseInt(age, 10), gender });
    }
  };

  return (
    <div className="pd-overlay">
      <div className="pd-modal">
        <button className="pd-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="pd-icon-wrap">
          <User size={28} />
        </div>
        <h2 className="pd-title">Patient Details</h2>
        <p className="pd-sub">
          Enter the patient information before starting the scan. These details
          will appear on the generated report.
        </p>

        <form onSubmit={handleContinue} noValidate>
          <label className="pd-field">
            <span className="pd-label">Patient Name</span>
            <div className={`pd-input-wrap ${errors.name ? 'pd-input-error' : ''}`}>
              <User size={15} className="pd-input-icon" />
              <input
                ref={nameRef}
                className="pd-input"
                placeholder="e.g. John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            </div>
            {errors.name && (
              <span className="pd-error"><AlertCircle size={13} />{errors.name}</span>
            )}
          </label>

          <label className="pd-field">
            <span className="pd-label">Age</span>
            <div className={`pd-input-wrap ${errors.age ? 'pd-input-error' : ''}`}>
              <Calendar size={15} className="pd-input-icon" />
              <input
                className="pd-input"
                placeholder="e.g. 34"
                inputMode="numeric"
                value={age}
                onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, ''))}
                autoComplete="off"
              />
            </div>
            {errors.age && (
              <span className="pd-error"><AlertCircle size={13} />{errors.age}</span>
            )}
          </label>

          <label className="pd-field">
            <span className="pd-label">Gender</span>
            <div className={`pd-input-wrap ${errors.gender ? 'pd-input-error' : ''}`}>
              <User size={15} className="pd-input-icon" />
              <select
                className="pd-input"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {errors.gender && (
              <span className="pd-error"><AlertCircle size={13} />{errors.gender}</span>
            )}
          </label>

          <button className="pd-continue" type="submit">
            <Check size={16} />
            Continue to Scan
          </button>
        </form>
      </div>
    </div>
  );
};

export default PatientDetailsModal;
